/* eslint-disable @typescript-eslint/no-explicit-any */
// Journal component interfaces and types
import {
  SmileyIcon as Happy,
  SmileyMehIcon as Calm,
  SmileyWinkIcon as Neutral,
  SmileySadIcon as Sad,
  SmileyNervousIcon as Nervous,
  ThumbsUpIcon,
  LoveIcon,
  InsightfulIcon,
  ClapIcon,
  SmileIcon,
  BadgeInspirationIcon as inspiration,
  BadgeProgressIcon as progress,
  BadgeThoughtsIcon as thoughts,
  FireIcon,
} from "../assets/icons";

// ✅ UPDATED: ReactionType matches backend exactly (no transformation needed)
export type ReactionType =
  | "thumbsUp"
  | "love"
  | "insightful"
  | "clap"
  | "smile"
  | "fire";

export interface PostReactions {
  thumbsUp: number;
  love: number;
  insightful: number;
  clap: number;
  smile: number;
  fire: number;
}

// ✅ UPDATED: Post interface matches backend response exactly
export interface Post {
  // Identification
  id: string;
  authorId: number;
  
  // Author information (flat structure, no nested 'author' object)
  userName: string;
  userAvatar: string;
  followerCount: number;
  
  // Content
  content: string;
  mood?: string;
  visibility: "followers" | "private";
  
  // Media - ✅ UPDATED: Simple string array (not PostImage objects)
  images?: string[];
  
  // Engagement
  reactions: PostReactions;
  userReaction?: ReactionType | null;
  replies: number;  // ✅ UPDATED: Now called 'replies' not 'comments_count'
  
  // Timestamps
  created_at: string;
  timestamp: string;  // Human-readable format (e.g., "2h ago")
  
  // Optional therapy data
  therapyData?: {
    period: "daily" | "weekly";
    date?: string;
    dateRange?: { start: string; end: string };
    sessions?: {
      coughSessions: number;
      chestSessions: number;
      totalMinutes: number;
      adherencePercentage: number;
    };
    weeklyProgress?: Array<{
      date: string;
      completed: boolean;
      sessions: number;
    }>;
    therapies?: any[];
  };
}

// ✅ DEPRECATED: PostData is now same as Post (no transformation needed)
// You can either:
// 1. Use Post everywhere, OR
// 2. Keep PostData as alias for backward compatibility
export type PostData = Post;

export interface PostCardProps {
  post: Post;
  onReact?: (postId: string, reaction: ReactionType) => void;
  onReply?: (postId: string) => void;
  showFooter?: boolean;
}

export interface PostEngagementProps {
  postId?: string;
  reactions: PostReactions;
  replies: number;
  userReaction?: ReactionType | null;
  timestamp: string;
  onReact: (reaction: ReactionType) => void;
  onReply: () => void;
}

export interface ReactionPickerProps {
  isOpen: boolean;
  onReactionSelect: (reaction: ReactionType) => void;
  onClose: () => void;
  triggerRef?: React.RefObject<HTMLElement> | any;
  activePointerId?: number | null;
}


export const REACTION_EMOJIS: Record<ReactionType, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
  thumbsUp: ThumbsUpIcon,
  love: LoveIcon,
  insightful: InsightfulIcon,
  clap: ClapIcon,
  smile: SmileIcon,
  fire: FireIcon, // You might need to add a FireIcon to your assets
};

export const REACTION_LABELS: Record<ReactionType, string> = {
  thumbsUp: "Like",
  love: "Love",
  insightful: "Insightful",
  clap: "Clap",
  smile: "Smile",
  fire: "Fire",
};

export interface ReflectionsModalCardProps {
  onOpenModal: () => void;
  userAvatar?: string;
  placeholder?: string;
  userName?: string;
  setPreSelected?: (
    selectedValue: "text" | "template" | "images" | "feelings" | null
  ) => void;
}

export interface JournalCategory {
  id: string;
  title: string;
  subtitle: string;
  iconSvg: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  onClick?: () => void;
}

export interface JournalCategoryCardProps {
  category: JournalCategory;
  className?: string;
  onClick?: (category: JournalCategory) => void;
}

export { inspiration, progress, thoughts };

export interface AddPostModalProps {
  journalCategories?: any;
  isOpen: boolean;
  onDidDismiss: (refresh?: boolean) => void;
  onPost: (
  post: Post
  ) => void;
  preSelected?: "text" | "template" | "images" | "feelings" | null;
  user?: any;
  setPreSelected?: (
    selectedValue: "text" | "template" | "images" | "feelings" | null
  ) => void;
}

export type SectionType =
  | "thoughts"
  | "inspiration"
  | "progress"
  | "goals"
  | null;

export interface Mood {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  label: string;
  color: string;
  labelColor: string;
}

export interface SectionCardsProps {
  journalCategories: string;
  activeSection: SectionType;
  onSectionChange: (section: SectionType) => void;
  onTherapyDataSelect?: (
    data: {
      period: "daily" | "weekly";
      date?: string;
      dateRange?: { start: string; end: string };
      sessions?: {
        coughSessions: number;
        chestSessions: number;
        totalMinutes: number;
        adherencePercentage: number;
      };
      weeklyProgress?: Array<{
        date: string;
        completed: boolean;
        sessions: number;
      }>;
      therapies?: any[];
    } | null
  ) => void;
  templateCards?: TemplateCard[];
  onTemplateSelect?: (templateKey: string) => void;
  toaster?: any;
}

export interface TemplateCard {
  key: string;
  title: string;
  description: string;
  template?: string;
  className?: string;
  inputCharIndex?: number;
}

export interface BottomActionsBarProps {
  fileInputRef?: React.RefObject<HTMLInputElement | null>;
  selectedAudience: string;
  selectedMood: string;
  onAudienceChange: (audience: string) => void;
  onMoodChange: (mood: string) => void;
  onImageSelect: (files: File[]) => void;
  showAudiencePopover: boolean;
  showMoodSelector: boolean;
  onToggleAudiencePopover: () => void;
  onToggleMoodSelector: () => void;
}

export const MOODS: Mood[] = [
  { icon: Happy, label: "Happy", color: "#E1F2FE", labelColor: "#0778CF" },
  { icon: Calm, label: "Calm", color: "#E0FFF4", labelColor: "#00B977" },
  { icon: Neutral, label: "Neutral", color: "#EEEBF4", labelColor: "#8575B3" },
  { icon: Sad, label: "Sad", color: "#EFEFF1", labelColor: "#6D7980" },
  { icon: Nervous, label: "Anxious", color: "#FFF0E0", labelColor: "#B65D00" },
];

export const AUDIENCES = ["Followers", "Private"];